<template>
  <el-dialog
    v-model="localVisible"
    @close="onClose"
    :close-on-press-escape="false"
    :show-close="false"
    align-center
    append-to-body
    destroy-on-close
    class="appointment-modal appointment-modal--create"
    width="500px"
  >
    <div class="appointment-modal-body">
      <button class="modal-close-btn" @click="onClose">×</button>
      <div class="appointment-header-visual">
        <div class="header-row">
          <div class="header-item">
            <el-icon><Calendar /></el-icon>
            <span>{{ displayDate }}</span>
            <span class="header-muted">
              ({{ formattedDate }}
              <span class="date-weekday" :class="{ weekend: isWeekend }">{{ weekdayLabel }}</span>)
            </span>
          </div>
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ headerTimeLabel }}</span>
          </div>
        </div>
        <div class="header-row">
          <div class="header-item">
            <el-icon><UserFilled /></el-icon>
            <span>{{ staffLabel }}</span>
          </div>
          <div class="header-item">
            <img src="/img/cabinet-modal.svg" class="cabinet-header-icon" alt="" aria-hidden="true" />
            <span>{{ form.employee.room_name }}</span>
          </div>
        </div>
        <div class="header-row status-row">
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ form.duration || totalDuration }} мин</span>
          </div>
        </div>
      </div>

      <el-form :model="form" label-position="top" class="appointment-form">
        <template v-if="form.client">
          <div class="client-info-card">
            <div class="client-row client-phone">
              <el-icon><Phone /></el-icon>
              <span>{{ form.client.phone }}</span>
            </div>
            <div class="client-row client-name">
              <el-icon><UserFilled /></el-icon>
              <span>{{ form.client.fio }}</span>
              <el-link @click="form.client = null" type="danger" class="client-selected-reset">Сбросить</el-link>
            </div>
          </div>
        </template>
        <el-form-item v-else label="Клиент">
          <el-autocomplete
            v-model="clientSearch"
            :fetch-suggestions="searchClientsFn"
            placeholder="Телефон или имя"
            @select="selectClient"
            @change="onClientInput"
            :trigger-on-focus="false"
            popper-class="appointment-client-search"
            style="width: 100%;"
          >
            <template #default="{ item }">
              <div v-if="item.create" class="autocomplete-create">
                <el-icon><Plus /></el-icon>
                <span>Создать клиента: <b>{{ item.value }}</b></span>
              </div>
              <div v-else class="autocomplete-client-item">
                <div class="client-row">
                  <el-icon><UserFilled /></el-icon>
                  <span>{{ item.fio }}</span>
                </div>
                <div class="client-row" v-if="item.phone">
                  <el-icon><Phone /></el-icon>
                  <span>{{ item.phone }}</span>
                </div>
              </div>
            </template>
          </el-autocomplete>
        </el-form-item>

        <el-form-item class="service-form-item">
          <ServiceMultiSelect
            v-model="form.services"
            :services="services"
            :remote="true"
            :city-id="form.employee?.city_id"
          />
        </el-form-item>

        <el-form-item label="Длительность">
          <div class="duration-control">
            <el-button class="duration-btn" @click="changeDuration(-15)">-</el-button>
            <div class="duration-value">{{ form.duration || 0 }} мин.</div>
            <el-button class="duration-btn" @click="changeDuration(15)">+</el-button>
          </div>
        </el-form-item>

        <el-form-item label="Комментарий">
          <el-input
            type="textarea"
            v-model="form.comment"
            placeholder="Комментарий"
          />
        </el-form-item>
      </el-form>
      <div class="modal-footer-actions">
        <el-button @click="onClose" class="btn-cancel">Отмена</el-button>
        <el-button type="primary" @click="save" :disabled="!canSave" class="btn-save">Записать</el-button>
      </div>
    </div>

    <QuickClientCreate
      :visible="showQuickCreate"
      :city-id="form.employee.city_id"
      :default-fio="quickCreateForm.fio"
      :default-phone="quickCreateForm.phone"
      @save="handleQuickCreateClient"
      @close="showQuickCreate = false"
    />
  </el-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import QuickClientCreate from '@/components/util/calendar/QuickClientCreate.vue'
import { Calendar, Clock, Phone, Plus, UserFilled } from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'

export default {
  name: 'AppointmentCreateModal',
  components: { Plus, Phone, ServiceMultiSelect, Clock, UserFilled, QuickClientCreate, Calendar },
  props: {
    visible: { type: Boolean, required: true },
    date: { type: String, required: true },
    time: { type: String, required: true, default: '' },
    employee: { type: Object, required: true }
  },
  data() {
    return {
      quickCreateForm: {
        fio: '',
        phone: ''
      },
      localVisible: this.visible,
      form: {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        serviceIds: [],
        duration: 0,
        client: null,
        comment: ''
      },
      clientSearch: '',
      showQuickCreate: false,
      lastSuggestions: [],
      showCreateClientButton: false,
      durationEdited: false
    }
  },
  computed: {
    displayDate() {
      if (!this.form.date) return ''
      const parts = this.form.date.split('-')
      if (parts.length !== 3) return this.form.date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    headerTimeLabel() {
      const duration = this.form.duration || this.totalDuration
      if (!this.form.time || !duration) return this.form.time
      return `${this.form.time}–${this.addMinutes(this.form.time, duration)}`
    },
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    ...mapState('clients', ['search_clients']),
    ...mapState('services', ['services']),
    canSave() {
      return this.form.services.length > 0 && this.form.client
    },
    staffLabel() {
      return this.form.employee?.user_email || this.form.employee?.email || this.form.employee?.user_name || ''
    },
    totalDuration() {
      return this.form.services.reduce((sum, s) => sum + (+s.duration || 0), 0)
    },
    formattedDate() {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      const [, month, day] = this.form.date.split('-')
      const monthName = months[parseInt(month, 10) - 1]
      return `${day} ${monthName}`
    }
  },
  watch: {
    visible(val) {
      this.localVisible = val
      if (val) this.resetForm()
    },
    localVisible(val) {
      if (!val) this.$emit('update:visible', false)
    },
    date(val) {
      this.form.date = val
    },
    time(val) {
      this.form.time = val
    },
    employee(val) {
      this.form.employee = val
    },
    'form.services': {
      handler() {
        if (!this.durationEdited) {
          this.form.duration = this.totalDuration
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients']),
    async searchClientsFn(query, cb) {
      if (!query || query.length < 3) return cb([])

      await this.searchClients({ query })
      let results = (this.search_clients || []).map(c => ({
        ...c,
        value: c.fio + (c.phone ? ` (${c.phone})` : '')
      }))

      this.lastSuggestions = results

      if (results.length === 0) {
        results = [{ value: query, create: true }]
      }

      cb(results)
    },
    onClientInput(val) {
      this.showCreateClientButton = val && val.length >= 3 && this.lastSuggestions.length === 0
    },
    selectClient(item) {
      if (item.create) {
        this.openQuickCreateClient(this.clientSearch)
      } else {
        this.form.client = item
        this.clientSearch = ''
        this.showCreateClientButton = false
      }
    },
    openQuickCreateClient(query) {
      let phone = ''
      let fio = ''
      if (/^\d[\d\-+()\s]+$/.test(query)) phone = query
      else fio = query
      this.quickCreateForm = { fio, phone }
      this.showQuickCreate = true
    },
    async handleQuickCreateClient(payload) {
      const res = await this.$store.dispatch('clients/saveQuickClient', payload)

      const newClient = res && res.id
        ? { id: res.id, fio: payload.fio, phone: payload.phone }
        : { fio: payload.fio, phone: payload.phone }
      this.form.client = newClient
      this.clientSearch = newClient.fio
      this.showQuickCreate = false
      this.showCreateClientButton = false
    },
    resetForm() {
      this.form = {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        duration: 0,
        client: null,
        comment: ''
      }
      this.clientSearch = ''
      this.showCreateClientButton = false
      this.lastSuggestions = []
      this.durationEdited = false
    },
    onClose() {
      this.localVisible = false
    },
    changeDuration(step) {
      this.durationEdited = true
      const next = (this.form.duration || 0) + step
      this.form.duration = Math.max(0, next)
    },
    addMinutes(time, min) {
      let [h, m] = time.split(':').map(Number)
      m += min
      h += Math.floor(m / 60)
      m = m % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    async save() {
      const totalDuration = this.form.duration || (this.form.services || []).reduce((sum, srv) => sum + (+srv.duration || 0), 0)
      const endTime = this.addMinutes(this.form.time, totalDuration)

      const appointment = {
        id: this.form.id || null,
        branch_id: this.form.employee.branch_id,
        city_id: this.form.employee.city_id,
        room_id: this.form.employee.room_id,
        user_id: this.form.employee.user_id,
        user_name: this.form.employee.user_name,
        user_email: this.form.employee.user_email || this.form.employee.email || '',
        client_id: this.form.client.id,
        client_name: this.form.client.fio,
        client_phone: this.form.client.phone,
        date: this.form.date,
        start_time: this.form.time,
        end_time: endTime,
        services: this.form.services,
        comment: this.form.comment,
        duration_time: totalDuration,
        status: 'future',
        color: 'var(--ui-color-brand-strong)'
      }
      await this.$store.dispatch('appointments/saveAppointment', appointment)
      this.onClose()
    }
  }
}
</script>

<style scoped>
.header-muted {
  color: #8fa5cb;
}

.client-row {
  min-width: 0;
}

.client-row span {
  min-width: 0;
}

.client-selected-reset {
  margin-left: auto;
  font-size: 13px;
  line-height: 1;
}

.duration-btn.el-button,
.duration-btn.el-button:hover,
.duration-btn.el-button:focus,
.duration-btn.el-button:active {
  --el-button-bg-color: #01102a !important;
  --el-button-hover-bg-color: #01102a !important;
  --el-button-active-bg-color: #01102a !important;
  --el-button-border-color: transparent !important;
  --el-button-hover-border-color: transparent !important;
  --el-button-active-border-color: transparent !important;
  --el-button-text-color: #00c3fd !important;
  --el-button-hover-text-color: #00c3fd !important;
  --el-button-active-text-color: #00c3fd !important;
  position: relative !important;
  isolation: isolate !important;
  background: #01102a !important;
  background-color: #01102a !important;
  background-image: none !important;
  border: 0 !important;
  box-shadow: inset 0 0 0 999px #01102a !important;
  color: #00c3fd !important;
}

.duration-btn.el-button::before {
  content: "" !important;
  position: absolute !important;
  inset: 0 !important;
  z-index: -1 !important;
  border-radius: inherit !important;
  background: #01102a !important;
}

.duration-btn.el-button > span {
  position: relative !important;
  z-index: 1 !important;
  color: #00c3fd !important;
  font-weight: 700 !important;
}

</style>
